import { Component, computed, inject, signal } from "@angular/core";
import { NetworkStatusService } from "../../../../shared/services/network-status.service";
import { ActivatedRoute, Router } from "@angular/router";
import { SkeletonModule } from "primeng/skeleton";
import SyncManager from "../../../../core/sync/sync-manager";
import { Subscription } from "rxjs";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { InspectionJobDetail } from "../../../../domains/jobs/models/InspectionJobDetail";
import { LoggerService } from "../../../../shared/services/logger.service";
import { TagModule } from "primeng/tag";
import { DataSyncMetadataService } from "../../../../core/sync/data-sync/data-sync-metadata.service";
import { BqStepDataServiceFactory } from "../../../../personas/shared/bq-step-data-service/bq-step-data-service-factory";
import { BqInspectionDataService } from "../../services/bq-inspection-data.service";
import { DummyBqInspectionDataService } from "../../services/dummy-bq-inspection-data-service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";

@Component({
  selector: "app-job-submission",
  imports: [SkeletonModule, ProgressSpinnerModule, TagModule],
  providers: [BqStepDataServiceFactory],
  templateUrl: "./job-submission.component.html",
  styleUrl: "./job-submission.component.scss"
})
export class JobSubmissionComponent {
  constructor(
    private networkService: NetworkStatusService,
    private router: Router,
    private route: ActivatedRoute,
    private syncService: SyncManager,
    private logger: LoggerService,
    private dataSyncMetadataService: DataSyncMetadataService,
    private bqStepDataServiceFactory: BqStepDataServiceFactory
  ) {
    this.onlineSubscription = this.networkService.online$.subscribe(val => {
      this.isOnline = val;
    });

    this.syncServiceSubscription = this.syncService.onSyncCompleted.subscribe(() => {
      this.syncDone = true;
      this.checkSpinner();
    });

    // Handle sync failures - keep job marked as complete but don't delete it
    this.syncFailedSubscription = this.syncService.onSyncFailed.subscribe(() => {
      this.syncDone = true;
      this.checkSpinner();
    });
  }

  private toastService = inject(UiToastService);
  private bqStepDataService = signal<BqInspectionDataService>(new DummyBqInspectionDataService(this.toastService));

  dataServiceJob = computed(() => this.bqStepDataService()?.job() ?? (() => {
    throw new Error("Job data not available in BqInspectionDataService");
  })());
  dataServiceLot = computed(() => this.bqStepDataService()?.lot() ?? (() => {
    throw new Error("Lot data not available in BqInspectionDataService");
  })());
  dataServiceComponents = computed(() => this.bqStepDataService()?.components() ?? []);
  dataServiceAreas = computed(() => this.bqStepDataService()?.areas() ?? []);
  dataServiceAssertions = computed(() => this.bqStepDataService()?.assertions() ?? []);
  dataServiceIssues = computed(() => this.bqStepDataService()?.assertionIssues() ?? []);
  dataServiceMedia = computed(() => this.bqStepDataService()?.media() ?? []);
  dataServiceAreasConfig = computed(() => this.bqStepDataService()?.areasConfig() ?? []);
  dataServiceZones = computed(() => this.bqStepDataService()?.zones() ?? []);

  assertions = computed(() => this.dataServiceAssertions());
  issues = computed(() => this.dataServiceIssues().filter(i => this.assertions().some(a => a.identifier === i.assertionIdentifier && i.deleted !== 1)));

  jobId = computed(() => this.dataServiceJob().id);
  inspectionJob = computed<InspectionJobDetail>(() => ({
    ...this.dataServiceLot(),
    ...this.dataServiceJob(),
    streetAddress: this.dataServiceJob().streetAddress ?? this.dataServiceLot().streetAddress,
  }));
  lotAddressLine1 = computed(() => `${this.inspectionJob().streetAddress}`);
  lotAddressLine2 = computed(() =>
    `${this.inspectionJob().salesCommunityName}` +
    " - " +
    `Lot ${this.inspectionJob().lotBlock}`
  );

  isOnline: boolean = true;
  submissionFinished: boolean = false;
  submittedDate: Date = new Date();

  private syncServiceSubscription!: Subscription;
  private syncFailedSubscription?: Subscription;
  private onlineSubscription?: Subscription;
  private minTimeElapsed = false;
  maxTimeoutElapsed = false;
  private syncDone = false;

  private readonly MIN_TIME = 3000; // 3s
  private readonly MAX_TIME = 30000; // 30s
  private maxSyncTimeoutId: number | null = null;

  async ngOnInit() {
    const bqStep = this.route.snapshot.parent?.data["bqStep"] as string;
    this.bqStepDataService.set(this.bqStepDataServiceFactory.getService<BqInspectionDataService>(bqStep));

    const ccmIssuesToCloneToCmIssues = this.bqStepDataService().isCcmInspection() ? this.issues() : [];
    await this.bqStepDataService().submitJob(this.dataServiceJob(), ccmIssuesToCloneToCmIssues);

    this.logger.event(`${bqStep}JobSubmitted`, { jobId: this.jobId, issuesCount: this.issues().length });

    if (this.isOnline) {
      setTimeout(() => {
        this.minTimeElapsed = true;
        this.checkSpinner();
      }, this.MIN_TIME);

      this.maxSyncTimeoutId = window.setTimeout(() => {
        this.maxTimeoutElapsed = true;
        this.logger.warn(`Max time elapsed for job submission spinner for job ${this.jobId}`);
        this.checkSpinner();
      }, this.MAX_TIME);

      this.logger.event(`${bqStep}JobSubmittedOnline`, { jobId: this.jobId, issuesCount: this.issues().length });
      this.syncService.manuallyTriggerSync();
    } else {
      this.logger.event(`${bqStep}JobSubmittedOffline`, { jobId: this.jobId, issuesCount: this.issues().length });
      this.dataSyncMetadataService.updateSyncMetadata();
    }
  }

  private checkSpinner() {
    // minimum time must pass, and EITHER sync is done OR max timeout hit
    if (this.minTimeElapsed && (this.syncDone || this.maxTimeoutElapsed)) {
      this.submissionFinished = true;
      if (this.maxSyncTimeoutId) {
        clearTimeout(this.maxSyncTimeoutId);
      }
    }
  }

  ngOnDestroy() {
    if (this.syncServiceSubscription) {
      this.syncServiceSubscription.unsubscribe();
    }
    this.syncFailedSubscription?.unsubscribe();
    this.onlineSubscription?.unsubscribe();
  }

  async clickOnDone() {
    this.router.navigate(["schedule"], { relativeTo: this.route.parent?.parent });
  }
}

